import type { FC, ReactNode } from 'react'
import type { ListItem } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'

import {
    AnimatePresence,
    motion,
    useInView,
    useScroll,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { finished } from '@/constants/content'

interface ChecklistRowProps {
    item: ListItem
    index: number
    onCheck: (index: number) => void
}

const CheckBox: FC<{ checked: boolean }> = ({ checked }): ReactNode => (
    <span
        aria-hidden='true'
        className={cn(
            'relative grid size-10 shrink-0 place-items-center rounded-md border-2 border-ink transition-colors duration-300 sm:size-12',
            checked ? 'bg-blaze-deep' : 'bg-paper'
        )}
    >
        <motion.span
            className='absolute inset-0 rounded-[4px] bg-blaze-deep'
            initial={false}
            animate={checked ? { scale: [0.4, 1.18, 1], opacity: 1 } : { scale: 0.4, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        />

        <svg viewBox='0 0 24 24' className='relative size-6 sm:size-7'>
            <motion.path
                d='M4.5 12.5 L10 18 L19.5 6.5'
                fill='none'
                stroke='var(--color-paper)'
                strokeWidth={3.2}
                strokeLinecap='round'
                strokeLinejoin='round'
                initial={false}
                animate={{ pathLength: checked ? 1 : 0 }}
                transition={{ duration: 0.4, delay: checked ? 0.12 : 0, ease: [0.65, 0, 0.35, 1] }}
            />
        </svg>
    </span>
)

// Each line ticks itself off as it crosses the middle of the screen, like a list of finished jobs.
const ChecklistRow: FC<ChecklistRowProps> = ({ item, index, onCheck }): ReactNode => {
    const ref = useRef<HTMLLIElement>(null)
    const checked = useInView(ref, { once: true, margin: '0px 0px -42% 0px' })

    useEffect(() => {
        if (checked) onCheck(index)
    }, [checked, index, onCheck])

    return (
        <li ref={ref} className='flex items-start gap-5 border-t-2 border-ink/15 py-6 sm:gap-7 sm:py-8'>
            <CheckBox checked={checked} />

            <p className='pt-1 text-lg leading-snug sm:pt-1.5 sm:text-2xl'>
                <strong className='font-heading font-bold'>
                    {`${item.title}:`}
                </strong>

                {' '}

                <span className='text-ink/75'>
                    {item.body}
                </span>
            </p>
        </li>
    )
}

const Counter: FC<{ count: number, total: number }> = ({ count, total }): ReactNode => (
    <div aria-hidden='true' className='flex items-end font-heading text-[8.5rem] font-extrabold leading-[0.8] tracking-[-0.05em] wdth-75 xl:text-[10rem]'>
        <span className='relative inline-block h-[0.8em] w-[0.62em] overflow-hidden'>
            <AnimatePresence initial={false}>
                <motion.span
                    key={count}
                    className={cn('absolute inset-x-0 bottom-0 text-center', count === total && 'text-blaze-deep')}
                    initial={{ y: '100%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '-100%' }}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                >
                    {count}
                </motion.span>
            </AnimatePresence>
        </span>

        <span className='text-ink/20'>
            /<span className='ml-[0.08em]'>{total}</span>
        </span>
    </div>
)

const Finished: FC = (): ReactNode => {
    const headingRef = useRef<HTMLHeadingElement>(null)
    const reduce = useReduceMotion()
    const [checkedRows, setCheckedRows] = useState<Array<number>>([])

    const { scrollYProgress } = useScroll({ target: headingRef, offset: ['start 85%', 'end 40%'] })
    const fillFirst = useTransform(scrollYProgress, [0.15, 0.55], ['0% 100%', '100% 100%'])
    const fillSecond = useTransform(scrollYProgress, [0.55, 1], ['0% 100%', '100% 100%'])

    const handleCheck = useCallback((index: number) => {
        setCheckedRows(rows => rows.includes(index) ? rows : [...rows, index])
    }, [])

    return (
        <section id='finished' className='relative overflow-x-clip bg-[#EEF2EF] py-24 sm:py-32 lg:py-40'>
            <div className='shell'>
                <h2
                    ref={headingRef}
                    className='max-w-[15ch] font-heading text-[11.5vw] font-bold leading-[0.9] tracking-[-0.015em] wdth-80 sm:text-[8.5vw] lg:text-[6.4vw] 2xl:text-[5.9rem]'
                >
                    {finished.heading.before}

                    {' '}

                    <motion.span
                        className='text-outline bg-[linear-gradient(var(--color-blaze-deep),var(--color-blaze-deep))] bg-no-repeat bg-clip-text [--outline-color:var(--color-blaze-deep)] [--outline-width:0] [-webkit-background-clip:text]'
                        style={{ backgroundSize: reduce ? '100% 100%' : fillFirst }}
                    >
                        {finished.heading.hollow}
                    </motion.span>

                    {' '}

                    {finished.heading.middle}

                    {' '}

                    <motion.span
                        className='text-outline bg-[linear-gradient(var(--color-blaze-deep),var(--color-blaze-deep))] bg-no-repeat bg-clip-text [--outline-color:var(--color-blaze-deep)] [--outline-width:0] [-webkit-background-clip:text]'
                        style={{ backgroundSize: reduce ? '100% 100%' : fillSecond }}
                    >
                        {finished.heading.solid}
                    </motion.span>
                </h2>

                <div className='mt-14 grid gap-12 lg:mt-20 lg:grid-cols-12 lg:gap-x-10 lg:gap-y-12'>
                    <div className='lg:col-span-4 lg:row-span-2'>
                        <div className='lg:sticky lg:top-28'>
                            <p className='max-w-[30rem] text-lg leading-relaxed text-ink/80 sm:text-xl'>
                                {finished.intro}
                            </p>

                            <div className='mt-12 hidden lg:block'>
                                <Counter count={checkedRows.length} total={finished.items.length} />
                            </div>
                        </div>
                    </div>

                    <div className='lg:col-span-7 lg:col-start-6'>
                        <ul className='border-b-2 border-ink/15'>
                            {finished.items.map((item, index) => (
                                <ChecklistRow key={item.title} item={item} index={index} onCheck={handleCheck} />
                            ))}
                        </ul>
                    </div>

                    <div className='lg:col-span-7 lg:col-start-6'>
                        <p className='font-heading text-3xl font-bold leading-[1.05] tracking-[-0.02em] sm:text-4xl lg:text-5xl'>
                            {finished.closing}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Finished
